import { Component } from '@angular/core';
import { TaskInfoService } from '../../services/task-info/task-info.service';
import { TaskbankColumnService } from 'src/app/modules/home/dashboard/task-bank/column-service/taskbank-column.service';
import { DataRepositoryService } from '../../services/data-repository/data-repository.service';
import { DataAccessService } from '../../services/data-access/data-access.service';
import { TaskMovementService } from '../../services/operations/task-movement.service';
import { take } from 'rxjs';
import { IPlannedPBMTasksMap } from '../../models/tasks-planned';
import { TBTabName } from '../../entity/enums';
import { OrderIdColorPipe } from '../../pipe/OrderIdColor/order-id-color.pipe';

@Component({
    selector: 'app-notification-pop-up',
    templateUrl: './notification-pop-up.component.html',
    styleUrls: ['./notification-pop-up.component.scss', '../../css/primeng-table.component.scss'],
    providers: [OrderIdColorPipe],
    standalone: false
})
export class NotificationPopUpComponent {
  popupVisible: boolean = false;
  title: string = 'Previous Day orders';
  previousDayOrders: any = [];
  visibleColumns: any = [];
  displayItemDialog: boolean = false;
  header: string = '';
  additionalData: any = {}

  constructor(
    private readonly taskInfoService: TaskInfoService,
    private readonly dataAccessService: DataAccessService,
    public readonly taskMovementService: TaskMovementService,
    private readonly taskBankColumnService: TaskbankColumnService,
    private readonly dataRepositoryService: DataRepositoryService
  ) { }

  ngOnInit() {
    this.visibleColumns = this.taskBankColumnService.columnConfigurations['Previous Day Orders'].filter(col => col.visible);
  }

  /**
   * Method show popup
   */
  showPreviousDayTasksDialog() {
    this.popupVisible = true;
    this.getPreviousDayData();
  }

  /**
  * Method calls API to get previous day tasks, 
  */
  getPreviousDayData() {
    this.taskInfoService.getPreviousDayTasksData(this.dataRepositoryService.selectedBusinessLocation);
    this.taskMovementService.resetTaskMovement();
    this.taskMovementService.isTaskMovementDone = true;

    this.dataAccessService.previousDayTaskUpdated$.pipe(take(1)).subscribe((previousDayTasks) => {
      if (previousDayTasks) {
        this.previousDayOrders = [...this.dataRepositoryService.previousDayDataCollection.values()];
      }
    });
  }

  reset() {
    this.taskMovementService.resetTaskMovement(); 
  }

    /**
   *  for task unselection from table row event
   */
  onRowUnselect(event: any): void {
    const taskId = event?.data?.task_id;
    if (taskId) {
      this.taskMovementService.onTaskUnselection(taskId);
    }
  }

  /**
   * Opens a dialog to edit either the manager notes or the address related to the specified task.
   * 
   * @param taskDetails - The details of the task to be edited.
   * @param type - Specifies whether to edit the 'address' or the 'manager' notes.
   */
  handleEditManagerNotesAddress(taskDetails: IPlannedPBMTasksMap, type: 'address' | 'manager') {
    this.taskInfoService.showDialog(taskDetails, type, 'PreviousDayPopup')
  }

  /**
   * Closes the dialog by delegating the close event to the taskInfoService.
   * 
   * @param $event - A boolean indicating whether to close the dialog (true) or not (false).
   */
  closeDialog($event: boolean) {
    this.taskInfoService.closeDialog($event);
  }

  /**
   * Setter for controlling the visibility of the popup dialog.
   * Updates the `showPopup` property in the `taskInfoService` to reflect
   * the current visibility state of the dialog.
   * 
   * @param value - A boolean indicating whether the popup should be shown (true) or hidden (false).
   */
  set getShowPopup(value: boolean) {
    if (this.taskInfoService?.updatePopup) {
      this.taskInfoService.updatePopup.showPopup = value;
    }
  }

  /**
   * Gets the task object that is currently being updated in the popup.
   * 
   * @returns The task being updated, or an empty string if none is set.
   */
  get getUpdateTask() {
    return this.taskInfoService?.updatePopup?.updateTask ?? '';
  }

  /**
   * Indicates whether the update popup dialog should be shown.
   * 
   * @returns True if the popup should be visible; otherwise false.
   */
  get getShowPopup() {
    return this.taskInfoService?.updatePopup?.showPopup ?? false;
  }

  /**
   * Gets the ID of the task that is currently being updated in the popup.
   * 
   * @returns The task ID being updated, or an empty string if none is set.
   */
  get getUpdateTaskID() {
    return this.taskInfoService?.updatePopup?.updateTask.task_id ?? '';
  }

  /**
   * Gets the header/title text of the update popup dialog.
   * 
   * @returns The dialog header text, or an empty string if none is set.
   */
  get DialogHeader() {
    return this.taskInfoService?.updatePopup?.dialogHeader ?? '';
  }

  /**
   * get the current dialog type from the updatePopup state in taskInfoService.
   * 
   * @returns The dialog type as a string, or an empty string if not set.
   */
  get DialogType() {
    return this.taskInfoService?.updatePopup?.dialogType ?? '';
  }

  /**
   * Redirect To PBM Portal to Edit the order information in PBM.
   * 
   * @param task - The pickup order task details to be edited.
   */
  editPickupOrder(task: IPlannedPBMTasksMap) {
    this.taskInfoService.editPickupOrder(task);
  }

  /**
   * Opens an item dialog if the given element contains items or goods based on the type.
   * Checks if the element has a positive count of items or goods, then triggers showing the item list.
   * 
   * @param element - Contains item or goods counts.
   * @param type - The type of content to display, either 'items' or 'goods'.
   */
  openItemDialog(element: any, type: string): void {
      const hasItems = type === 'items' && element.item_count > 0;
      const hasGoods = type === 'goods' && element?.goods_count > 0;

      if (hasItems || hasGoods) {
        this.showItemList(element, type);
      }
  }
  
  onTableSelectionChange(selection:any[]) {
    const previousSelection = ((this.taskMovementService.selectedOptionsForTaskMove ?? []) as IPlannedPBMTasksMap[]).filter(Boolean);
    const previousSelectionIds = new Set(previousSelection.map((task) => task.task_id));
    const normalizedSelection = (selection ?? []).filter(Boolean) as IPlannedPBMTasksMap[];
    const currentSelectionIds = new Set(normalizedSelection.map((task) => task.task_id));
    const removedTasks = previousSelection.filter((task) => !currentSelectionIds.has(task.task_id));
    const addedTask = normalizedSelection.find(
      (task) => task?.isMovementAllowed && !previousSelectionIds.has(task.task_id)
    );

    this.taskMovementService.selectedOptionsForTaskMove = normalizedSelection;

    if (removedTasks.length) {
      removedTasks.forEach((task) => this.taskMovementService.onTaskUnselection(task.task_id));
    }

    if (addedTask) {
      this.taskMovementService.onTaskSelection(addedTask);
      this.taskMovementService.selectedOptionsForTaskMove =
      this.taskMovementService.selectedOptionsForTaskMove.filter(Boolean);
    }
  }

  /**
   * Display the item or goods list dialog with the appropriate header and data.
   * Sets the dialog header, populates additional data including the element, title, type, and default tab,
   * and makes the dialog visible.
   * 
   * @param element - selected item or goods container.
   * @param type - to display, either 'items' or 'goods'.
   */
  showItemList(element: any, type: string) {
      this.header = type === 'goods' ? 'Goods' : 'Items';
      this.additionalData = {
        element: element,
        title: type === 'goods' ? 'Goods' : 'Items',
        type: type,
        tab: TBTabName.Unplanned
      };
      this.displayItemDialog = true;
  }

}
